import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { en } from "./en";
import { tr, type MessageKey, type Messages } from "./tr";

export type Lang = "tr" | "en";
export type { MessageKey };

export const MESSAGES: Record<Lang, Messages> = { tr, en };
export const LANGS: Lang[] = ["tr", "en"];
const STORAGE_KEY = "fts.lang";

export type TVars = Record<string, string | number>;
export type TFn = (key: MessageKey, vars?: TVars) => string;

/** Fill {name} placeholders; unknown placeholders are left as-is so they are visible in review. */
export function interpolate(template: string, vars?: TVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

export function translate(lang: Lang, key: MessageKey, vars?: TVars): string {
  return interpolate(MESSAGES[lang][key] ?? tr[key] ?? key, vars);
}

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "tr" || v === "en") return v;
  } catch {
    // Storage may be unavailable (private mode, blocked cookies); fall back to default.
  }
  return "tr";
}

type I18nValue = { lang: Lang; setLang: (l: Lang) => void; t: TFn; locale: string };

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? readStoredLang());

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // Ignore: the choice simply won't persist.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      setLang,
      t: (key, vars) => translate(lang, key, vars),
      locale: lang === "tr" ? "tr-TR" : "en-GB",
    }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

/** Shorthand for components that only need t(). */
export function useT(): TFn {
  return useI18n().t;
}
